import { useState } from "react";
import styled from "styled-components";
import css from "@styled-system/css";
import SwipeableViews from "react-swipeable-views";

import Box from "../Box/Box";
import {
  FeaturedProducts,
  ProductImage,
  FeaturedProductProps,
  FeaturedProductPanelProps,
} from "../FeaturedProductPanel/FeaturedProductPanel";
import ProductBlock from "./ProductBlock";
import ResponsiveLayout from "../ResponsiveLayout/ResponsiveLayout";

const TimelineProducts = styled((props) => <FeaturedProducts {...props} />)`
  ${css({
    display: ["none", "none", "flex"],
    width: "auto",
    flexDirection: "column",
    justifyContent: "center",
    pr: "92px",
    m: 0,
  })}
`;

const SwipeablePanel = styled((props) => <SwipeableViews {...props} />)`
  ${css({
    display: ["block", "block", "none"],
    width: "auto",
    flexDirection: "row",
    justifyContent: "flex-start",
    pr: "calc(100% - 250px)",
    pl: [6, "44px"],
    m: 0,
    mb: [9, "52px"],
  })}
`;

const sampleProducts: FeaturedProductProps[] = [
  {
    id: "cbd4102f-46af-4c76-804b-b27266b7fdee",
    title: "Lorem ipsum dolor sit amet consectetur",
    description:
      "Lorem ipsum dolor sit amet, consectetur adi pisicing elit, sed do eiusmod tempor incidi du nt ut labore et dolore magna aliqua.",
    imageUrl:
      "https://images.unsplash.com/photo-1494548162494-384bba4ab999?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&w=1000&q=80",
  },
  {
    id: "4e79156a-a1e9-466b-81f9-f2a4a1039ea7",
    title: "Lorem ipsum dolor sit amet consectetur",
    description:
      "Lorem ipsum dolor sit amet, consectetur adi pisicing elit, sed do eiusmod tempor incidi du nt ut labore et dolore magna aliqua.",
    imageUrl:
      "https://www.victorshamas.com/uploads/5/1/4/0/5140560/nature-human_orig.jpg",
  },
];

const ProductsPanel = ({
  products,
  backgroundColor,
  descriptionColor,
  paddingY = { _: "44px", desktop: "60px" },
  ...props
}: FeaturedProductPanelProps) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [prevIndex, setPrevIndex] = useState<number>(0);
  const onChangeIndex = (index: number) => {
    setActiveIndex((prevIndex) => {
      setPrevIndex(prevIndex);
      return index;
    });
  };

  return (
    <ResponsiveLayout
      width="100%"
      height="100%"
      display="flex"
      flexDirection={["column", "column", "row"]}
      paddingY={paddingY}
      {...props}
    >
      <TimelineProducts role="tablist">
        {products.map((product, index) => (
          <ProductBlock
            key={product.id}
            product={product}
            productsNumber={products.length}
            index={index}
            prevIndex={prevIndex}
            activeIndex={activeIndex}
            setPrevIndex={setPrevIndex}
            setActiveIndex={setActiveIndex}
            backgroundColor={backgroundColor}
            descriptionColor={descriptionColor}
          />
        ))}
      </TimelineProducts>
      <SwipeablePanel
        slideStyle={{
          backfaceVisibility: "hidden",
          perspective: "1000",
          width: "auto",
        }}
        index={activeIndex}
        onChangeIndex={onChangeIndex}
        enableMouseEvents
        role="tablist"
      >
        {products.map((product, index) => (
          <ProductBlock
            key={product.id}
            product={product}
            productsNumber={products.length}
            index={index}
            prevIndex={prevIndex}
            activeIndex={activeIndex}
            setPrevIndex={setPrevIndex}
            setActiveIndex={setActiveIndex}
            backgroundColor={backgroundColor}
            descriptionColor={descriptionColor}
            htmlTag="div"
          />
        ))}
      </SwipeablePanel>
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        maxWidth={{ desktop: "50%", tablet: "100%" }}
        marginLeft={{ desktop: "auto", tablet: "0" }}
      >
        <ProductImage
          products={products}
          activeProductIndex={activeIndex}
          containerHeight={["100%", "100%", "518px"]}
        />
      </Box>
    </ResponsiveLayout>
  );
};

const getProductsToRender = (products: FeaturedProductProps[]) => {
  if (products.length > 4) {
    return products.slice(0, 4);
  }

  if (products.length < 2) {
    return [...products, ...sampleProducts].slice(0, 2);
  }

  return products;
};

export default function TimelinePanel({
  products,
  backgroundColor = "secondary-nav-blue",
  paddingY = ["44px", "44px", "60px"],
  descriptionColor = "cta-primary-text-color-default",
  ...props
}: FeaturedProductPanelProps) {
  const productsToRender = getProductsToRender(products);

  return (
    <Box backgroundColor={backgroundColor} height={["100vh", "100vh", "auto"]}>
      <ProductsPanel
        products={productsToRender}
        backgroundColor={backgroundColor}
        descriptionColor={descriptionColor}
        paddingY={paddingY}
        {...props}
      />
    </Box>
  );
}
